import type { PortalUserRow } from '@/lib/portalUsers';

/** The four things a Super Admin can do to somebody on the Users page. */
export type UserAction = 'changeRole' | 'remove' | 'resend' | 'revoke';

export interface RowActions {
  actions: UserAction[];
  /**
   * Why a row that might be expected to offer something offers nothing, in
   * words. Null when there is nothing worth saying - a past row, or the whole
   * page being read-only, which the page explains once at the top.
   */
  note: string | null;
}

/** Said on the only Super Admin's row, in place of the actions it cannot have. */
export const LAST_SUPER_ADMIN_NOTE =
  'An organization must keep at least one Super Admin.';

const NOTHING: RowActions = { actions: [], note: null };

/** True when this is the one active Super Admin the organization has left. */
function isOnlySuperAdmin(row: PortalUserRow, rows: PortalUserRow[]): boolean {
  if (row.role !== 'SUPER_ADMIN' || row.state !== 'active') return false;
  const active = rows.filter((r) => r.role === 'SUPER_ADMIN' && r.state === 'active');
  return active.length === 1;
}

/**
 * What the Users page offers on one row.
 *
 * Worked out from the whole list and not the row alone, because "the only
 * Super Admin" is a fact about the list. The backend enforces the same rule
 * and has the last word - the list this reads may be stale - so this decides
 * only what is offered, never what is allowed.
 *
 * | Row                                      | Offered              |
 * | ---------------------------------------- | -------------------- |
 * | Active, not me, not the only Super Admin | Change role, Remove  |
 * | Active, the only Super Admin             | nothing, and why     |
 * | Active, me                               | nothing              |
 * | Invited or Expired                       | Resend, Revoke       |
 * | Revoked or Removed                       | nothing              |
 * | Any row, organization not writable       | nothing (the only    |
 * |                                          | Super Admin's note   |
 * |                                          | stays)               |
 */
export function actionsFor(
  row: PortalUserRow,
  rows: PortalUserRow[],
  writable: boolean,
): RowActions {
  if (row.isPast) return NOTHING;

  // Nothing can be changed at all - the organization is suspended, or staff
  // are viewing it - but the only Super Admin's row still says why it would
  // not offer anything anyway, because that is true either way.
  if (!writable) {
    return isOnlySuperAdmin(row, rows)
      ? { actions: [], note: LAST_SUPER_ADMIN_NOTE }
      : NOTHING;
  }

  if (row.state === 'active') {
    // The last-Super-Admin rule is checked first, so when the only Super
    // Admin is also me the row says why rather than only being quiet. Both
    // rules agree that nothing is offered.
    if (isOnlySuperAdmin(row, rows)) {
      return { actions: [], note: LAST_SUPER_ADMIN_NOTE };
    }
    // Own row: no remove (the backend refuses it outright) and no role change
    // (a slip here locks the person out of the page they are on).
    if (row.isSelf) return NOTHING;
    return { actions: ['changeRole', 'remove'], note: null };
  }

  if (row.state === 'invited' || row.state === 'expired') {
    // Nothing to resend or revoke without a link to replace or call off.
    if (!row.invitationId) return NOTHING;
    // Revoke on Expired as well: the backend allows it, and a Super Admin who
    // does not want the person in has no other way to clear the row.
    return { actions: ['resend', 'revoke'], note: null };
  }

  return NOTHING;
}
